/*  Responsibility: Wire up the LMS result page.  */
import { trackPageView } from '@finn-no/pulse-sdk';
import paginationTemplate from '../../../WEB-INF/templates/pagination.mustache';
import lmsResultTemplate from '../../../WEB-INF/templates/results.mustache';
import { filterBannerPublishEvent, initialBannerPublishEvent } from '../../banners/podletMessageBusEvents';
import checkboxState from '../../filters/checkboxState.js';
import setupDepartureDateFilter from '../../filters/departureDateFilter.js';
import durationDaysFormatter from '../../filters/durationDaysFormatter.js';
import setupFilterOptionsToggler from '../../filters/filterOptionsToggler.js';
import setupLocationTitle from '../../filters/locationTitle.js';
import setupMinimumRatingFilter from '../../filters/minimumRatingFilter.js';
import setupFilterLoadIndicator from '../../filters/modules/filterLoadIndicator.js';
import setupLinkToCheckboxAndRadioButtonTrigger from '../../filters/modules/linkToCheckboxAndRadioButtonTrigger.js';
import setupPagination from '../../filters/modules/pagination.js';
import priceFormatter from '../../filters/modules/priceFormatter.js';
import refreshResultsOnFilterChange from '../../filters/modules/refreshResultsOnFilterChange.js';
import setupPaleCheckboxFilter from '../../filters/paleCheckboxFilter.js';
import paleSliderFilter from '../../filters/paleSliderFilter.js';
import setupContinentTaxonomyFilter from '../../filters/taxonomyWithContinentFilter.js';
import dom from '../../lib/dom.js';
import setupModalWindow from '../../modal/modalWindow.js';
import prepareFilter from '../../pages/resultPage/prepareFiltersForView.js';
import resultCounter from '../../pages/resultPage/resultCounter.js';
import prepareLmsResultsForView from '../../pages/resultPage/resultPreparer.js';
import setupSaveSearch from '../../pages/resultPage/saveSearch.js';
import scrollToTop from '../../pages/resultPage/scrollToTop.js';
import toggleEmptyWarning from '../../pages/resultPage/showFiltersOrEmptyWarning.js';
import viewRenderer from '../../pages/resultPage/viewRenderer.js';
import LmsServer from '../../server/server.js';
import setupTrackDropIn from '../../tracking/trackDropIn.js';

const T = FINN.travel;
const P = FINN.travel.pp;

const airports = (airportList) =>
    airportList.map((airport) => ({
        name: 'airport',
        value: airport,
    }));

// Only the most specific locations should be kept
// The locationArray is sorted, so if current value starts with the previous value, the previous value should be removed
const keepMostSpecific = (locationArray) =>
    locationArray.reduce((accumulator, currentValue) => {
        if (accumulator.length === 0) {
            return [currentValue];
        }
        const lastValue = accumulator.pop();

        if (!currentValue.startsWith(lastValue)) {
            accumulator.push(lastValue);
        }

        accumulator.push(currentValue);

        return accumulator;
    }, []);

const locations = (locationArray) =>
    keepMostSpecific(locationArray).map((location) => ({
        name: 'location',
        value: location,
    }));

const suppliers = (supplierList) =>
    supplierList.map((supplier) => ({
        name: 'supplier',
        value: supplier,
    }));

// Pulse tracker id: TRL_pv01 (see tracking section in README.md)
const trackPage = (filters, numFound) =>
    trackPageView({
        object: {
            type: 'Listing',
            url: 'https://www.finn.no/reise/restplasser/',
            category: 'travel > last minute',
            layout: 'List',
            sortingType: filters.sorter ?? 'pris_lms',
            numItems: numFound,
            pageNumber: filters.pageNumber ? Number.parseInt(filters.pageNumber, 10) : 1,
            selectionFilters: [
                {
                    name: 'departureDateRange',
                    value: filters.avreise,
                },
                {
                    name: 'durationRange',
                    value: filters.lengde,
                },
                {
                    name: 'type',
                    value: filters.type?.join(','),
                },
                {
                    name: 'priceRange',
                    value: filters.pris,
                },
                {
                    name: 'stars',
                    value: filters.stjerner?.toString(),
                },
            ]
                .concat(airports(filters.fra || []))
                .concat(locations(filters.til || []))
                .concat(suppliers(filters.med || []))
                .filter((filter) => filter.name && filter.value), // Remove all filters where either name or value is not set
        },
        vertical: {
            name: 'travel',
            subVertical: 'last_minute',
        },
    });

export function setup(eventHub, resultViewCriteria, calendar) {
    const server = new LmsServer(T.contextPath);

    const getOffers = server.getOffers.bind(server, resultViewCriteria);

    const data = {};

    let destinationCity = '';
    let destinationCountry = ''; // Used for new podlet banners

    if (T.location.searchPhrase) {
        Object.assign(data, { searchPhrase: [T.location.searchPhrase] });
    }
    if (T.location.locationTaxonomy) {
        const deepestGeo = T.location.locationTaxonomy.split(',').reduce((a, b) => (a.split(';').length > b.split(';').length ? a : b));
        const [continent, country, region, city] = deepestGeo ? deepestGeo.split(';') : [];
        destinationCity = city || '';
        destinationCountry = country || '';
        Object.assign(data, { continent, country, region, city });
    }

    initialBannerPublishEvent({
        pageType: 'result',
        city: destinationCity,
        country: destinationCountry,
    });

    eventHub.subscribe('changed-results', (results) => {
        trackPage(resultViewCriteria, results.numFound);
    });

    eventHub.subscribe('polling-complete', () => {
        eventHub.subscribe('resultViewCriteria:changed', () => {
            const locationValue = resultViewCriteria.til;
            if (locationValue && locationValue.length) {
                const deepestGeo = locationValue.reduce((a, b) => (a.split(';').length > b.split(';').length ? a : b));
                const geoArray = deepestGeo ? deepestGeo.split(';') : [];
                destinationCity = geoArray[3] || '';
                destinationCountry = geoArray[1] || '';
            } else {
                // Location is empty, so reset values
                destinationCity = '';
                destinationCountry = '';
            }
            filterBannerPublishEvent({
                ...resultViewCriteria,
                page: resultViewCriteria.pageNumber,
                city: destinationCity,
                country: destinationCountry,
            });
        });
    });

    setupPagination({
        pagination: dom.byId('pagination-bottom'),
        resultViewCriteria,
        eventHub,
        template: paginationTemplate,
    });

    viewRenderer({
        root: dom.byId('lmsResults'),
        template: lmsResultTemplate,
        eventHub,
        dataPreparer: prepareLmsResultsForView,
    });

    resultCounter({
        counters: dom.byQuerySelector('[data-numberOfResults]', document),
        fieldName: 'numFound',
        eventHub,
    });

    setupFilterLoadIndicator({
        progressElements: dom.byQuerySelector('[data-filter-load-indicator]', document),
        eventHub,
    });

    setupDepartureDateFilter({
        dateInput: dom.byId('departureDate'),
        flexibleDeparture: dom.byId('flexibleDeparture'),
        createCalendar: calendar,
        resultViewCriteria,
        eventHub,
        urlKey: 'avreise',
        urlQueryParams: T.urlQueryParams,
    });

    paleSliderFilter({
        root: dom.byId('durationFilter'),
        fromInput: dom.byId('durationFrom'),
        toInput: dom.byId('durationTo'),
        resultViewCriteria,
        eventHub,
        formatter: durationDaysFormatter,
        resultViewName: 'duration',
        urlKey: 'lengde',
        urlQueryParams: T.urlQueryParams,
    });

    paleSliderFilter({
        root: dom.byId('priceFilter'),
        fromInput: dom.byId('priceFrom'),
        toInput: dom.byId('priceTo'),
        resultViewCriteria,
        eventHub,
        formatter: priceFormatter,
        resultViewName: 'price',
        urlKey: 'pris',
        urlQueryParams: T.urlQueryParams,
    });

    setupMinimumRatingFilter({
        root: dom.byId('minimumRatingFilter'),
        resultViewCriteria,
        eventHub,
        activeClass: 'icon-vurdering2',
        inactiveClass: 'icon-vurdering1',
        defaultActiveElements: 1,
        urlKey: 'stjerner',
        urlQueryParams: T.urlQueryParams,
    });

    setupPaleCheckboxFilter({
        root: dom.byId('supplierFilter'),
        template: dom.byId('supplierFilterTemplate').innerHTML,
        resultViewCriteria,
        eventHub,
        dataKey: 'supplierFacet',
        updateState: checkboxState,
        dataPreparer: prepareFilter.supplier,
        urlKey: 'med',
        urlQueryParams: T.urlQueryParams,
    });

    setupPaleCheckboxFilter({
        root: dom.byId('airportFilter'),
        template: dom.byId('airportFilterTemplate').innerHTML,
        resultViewCriteria,
        eventHub,
        dataKey: 'airportFacet',
        updateState: checkboxState,
        dataPreparer: prepareFilter.airport,
        urlKey: 'fra',
        urlQueryParams: T.urlQueryParams,
    });

    setupPaleCheckboxFilter({
        root: dom.byId('tripTypeFilter'),
        template: dom.byId('tripTypeFilterTemplate').innerHTML,
        resultViewCriteria,
        eventHub,
        dataKey: 'typeFacet',
        updateState: checkboxState,
        dataPreparer: prepareFilter.tripType,
        urlKey: 'type',
        urlQueryParams: T.urlQueryParams,
    });

    setupContinentTaxonomyFilter({
        root: dom.byId('locationFilter'),
        resultViewCriteria,
        eventHub,
        dataKey: 'locationFacet',
        urlKey: 'til',
        urlQueryParams: T.urlQueryParams,
        togglerSetup: setupFilterOptionsToggler,
        renderedDestinations: 'locationsHtml',
    });

    refreshResultsOnFilterChange({
        getResults: getOffers,
        eventHub,
    });

    scrollToTop({
        pagination: dom.byId('pagination-bottom'),
        _window: window,
    });

    setupLinkToCheckboxAndRadioButtonTrigger();

    eventHub.publish('first-stats', P.initialResult.stats);
    eventHub.publish('first-results', P.initialResult);

    eventHub.publish('changed-results', P.initialResult, { firstResult: true });
    eventHub.publish('polling-complete');

    toggleEmptyWarning({
        result: P.initialResult,
        root: document.body,
        eventHub,
    });

    setupLocationTitle({
        browserWindow: window,
        eventHub,
        resultViewCriteria,
        siteName: 'Restplasser',
    });

    setupModalWindow({
        root: dom.byId('modalWindowBackdrop'),
        container: dom.byId('modalWindowContainer'),
        content: dom.byId('modalWindowContent'),
        cancel: dom.byId('modalCancelButton'),
        confirm: dom.byId('modalConfirmButton'),
        eventHub,
    });

    setupSaveSearch({
        root: dom.byId('modalWindowContent'),
        showModal: [dom.byId('saveSearchButtonMobile'), dom.byId('saveSearchButtonDesktop')],
        template: dom.byId('saveSearchTemplate').innerHTML,
        subMarket: 'Restplasser',
        browserWindow: window,
        saveSearch: server.sendSaveSearch.bind(server),
        eventHub,
        resultViewCriteria,
    });

    setupTrackDropIn({
        root: dom.byId('lmsResults'),
    });
}
